import React, { useEffect, useState, useCallback } from 'react';
import {
  Typography, Button, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, Chip, Alert, Switch,
  FormControlLabel, Box, MenuItem,
} from '@mui/material';
import { Add, Edit, Delete } from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';

interface UnitBrief {
  id: number;
  name: string;
  code: string;
  unit_type: string;
}

interface AgencyBrief {
  id: number;
  name: string;
  code: string;
}

interface Station {
  id: number;
  tenant_id: number;
  agency_id: number | null;
  name: string;
  code: string;
  address: string | null;
  city: string | null;
  state: string | null;
  zip_code: string | null;
  latitude: number | null;
  longitude: number | null;
  is_active: boolean;
  notes: string | null;
  units: UnitBrief[];
}

const EMPTY_FORM = {
  name: '', code: '', agency_id: '' as string, address: '', city: '', state: '',
  zip_code: '', latitude: '', longitude: '', notes: '',
};

export default function StationsPage() {
  const { tenantId } = useAuth();
  const [stations, setStations] = useState<Station[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [agencies, setAgencies] = useState<AgencyBrief[]>([]);

  const loadAgencies = useCallback(async () => {
    if (!tenantId) return;
    try {
      const res = await api.get(`/api/tenants/${tenantId}/agencies`);
      setAgencies(res.data);
    } catch { /* ignore */ }
  }, [tenantId]);

  const load = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    try {
      const res = await api.get(`/api/tenants/${tenantId}/stations`);
      setStations(res.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load stations');
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => { load(); loadAgencies(); }, [load, loadAgencies]);

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setDialogOpen(true);
  };

  const openEdit = (s: Station) => {
    setEditingId(s.id);
    setForm({
      name: s.name, code: s.code, agency_id: s.agency_id?.toString() || '',
      address: s.address || '',
      city: s.city || '', state: s.state || '', zip_code: s.zip_code || '',
      latitude: s.latitude?.toString() || '', longitude: s.longitude?.toString() || '',
      notes: s.notes || '',
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!tenantId) return;
    const payload: Record<string, unknown> = {
      name: form.name, code: form.code,
      agency_id: form.agency_id ? parseInt(form.agency_id) : null,
      address: form.address || null, city: form.city || null,
      state: form.state || null, zip_code: form.zip_code || null,
      latitude: form.latitude ? parseFloat(form.latitude) : null,
      longitude: form.longitude ? parseFloat(form.longitude) : null,
      notes: form.notes || null,
    };
    try {
      if (editingId) {
        await api.put(`/api/tenants/${tenantId}/stations/${editingId}`, payload);
      } else {
        await api.post(`/api/tenants/${tenantId}/stations`, payload);
      }
      setDialogOpen(false);
      load();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Save failed');
    }
  };

  const handleDelete = async (id: number) => {
    if (!tenantId || !window.confirm('Delete this station?')) return;
    try {
      await api.delete(`/api/tenants/${tenantId}/stations/${id}`);
      load();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Delete failed');
    }
  };

  const handleToggleActive = async (s: Station) => {
    if (!tenantId) return;
    try {
      await api.put(`/api/tenants/${tenantId}/stations/${s.id}`, { is_active: !s.is_active });
      load();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Update failed');
    }
  };

  const updateField = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm(prev => ({ ...prev, [field]: e.target.value }));

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h4">Stations</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={openCreate}>Add Station</Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Code</TableCell>
              <TableCell>Agency</TableCell>
              <TableCell>Address</TableCell>
              <TableCell>City</TableCell>
              <TableCell>Units</TableCell>
              <TableCell>Active</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={8} align="center">Loading...</TableCell></TableRow>
            ) : stations.length === 0 ? (
              <TableRow><TableCell colSpan={8} align="center">No stations configured</TableCell></TableRow>
            ) : stations.map(s => (
              <TableRow key={s.id}>
                <TableCell>{s.name}</TableCell>
                <TableCell>{s.code}</TableCell>
                <TableCell>{agencies.find(a => a.id === s.agency_id)?.name || '—'}</TableCell>
                <TableCell>{s.address}</TableCell>
                <TableCell>{s.city}</TableCell>
                <TableCell>
                  {s.units.map(u => (
                    <Chip key={u.id} label={`${u.code} (${u.unit_type})`} size="small" sx={{ mr: 0.5, mb: 0.5 }} />
                  ))}
                </TableCell>
                <TableCell>
                  <Switch checked={s.is_active} onChange={() => handleToggleActive(s)} size="small" />
                </TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => openEdit(s)}><Edit fontSize="small" /></IconButton>
                  <IconButton size="small" color="error" onClick={() => handleDelete(s.id)}><Delete fontSize="small" /></IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingId ? 'Edit Station' : 'Add Station'}</DialogTitle>
        <DialogContent>
          <TextField label="Name" fullWidth margin="dense" value={form.name} onChange={updateField('name')} required />
          <TextField label="Code" fullWidth margin="dense" value={form.code} onChange={updateField('code')} required
            helperText="Unique station identifier (e.g., STA1)" />
          <TextField label="Agency" fullWidth margin="dense" select
            value={form.agency_id} onChange={updateField('agency_id')}>
            <MenuItem value="">— No Agency —</MenuItem>
            {agencies.map(a => <MenuItem key={a.id} value={a.id.toString()}>{a.name}</MenuItem>)}
          </TextField>
          <TextField label="Address" fullWidth margin="dense" value={form.address} onChange={updateField('address')} />
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField label="City" margin="dense" value={form.city} onChange={updateField('city')} sx={{ flex: 2 }} />
            <TextField label="State" margin="dense" value={form.state} onChange={updateField('state')} sx={{ flex: 1 }} />
            <TextField label="ZIP" margin="dense" value={form.zip_code} onChange={updateField('zip_code')} sx={{ flex: 1 }} />
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField label="Latitude" margin="dense" value={form.latitude} onChange={updateField('latitude')} type="number" sx={{ flex: 1 }} />
            <TextField label="Longitude" margin="dense" value={form.longitude} onChange={updateField('longitude')} type="number" sx={{ flex: 1 }} />
          </Box>
          <TextField label="Notes" fullWidth margin="dense" value={form.notes} onChange={updateField('notes')} multiline rows={2} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={!form.name || !form.code}>Save</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
